import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Phone, MessageCircle, Mail, IndianRupee, Search, Loader2,
  CheckCircle2, AlertTriangle, Clock, NotebookPen, History as HistoryIcon, ExternalLink,
  BellRing,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const RS = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const AGING_META = {
  'overdue_30+':   { label: 'Overdue 30+ days', color: 'bg-red-600 text-white',  rank: 0 },
  'overdue_8_30':  { label: 'Overdue 8–30 days', color: 'bg-red-500 text-white', rank: 1 },
  'overdue_1_7':   { label: 'Overdue 1–7 days', color: 'bg-orange-500 text-white', rank: 2 },
  'due_today':     { label: 'Due Today', color: 'bg-amber-500 text-white', rank: 3 },
  'due_this_week': { label: 'Due This Week', color: 'bg-amber-200 text-amber-900', rank: 4 },
  'upcoming':      { label: 'Upcoming', color: 'bg-slate-200 text-slate-700', rank: 5 },
  'no_due_date':   { label: 'No Due Date', color: 'bg-slate-100 text-slate-500', rank: 6 },
};

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'easebuzz', label: 'Easebuzz' },
  { value: 'other', label: 'Other' },
];

export default function PaymentFollowup() {
  const { API } = useAuth();
  const [type, setType] = useState('recurring');
  const [search, setSearch] = useState('');
  const [agingFilter, setAgingFilter] = useState('all');
  const [data, setData] = useState({ rows: [], count: 0, total_amount: 0, aging_counts: {} });
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [markPaid, setMarkPaid] = useState({ open: false, row: null });
  const [waDialog, setWaDialog] = useState({ open: false, row: null });
  const [emailDialog, setEmailDialog] = useState({ open: false, row: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, row: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, row: null, records: [], notes: [] });

  // Forms
  const today = new Date().toISOString().slice(0, 10);
  const [payForm, setPayForm] = useState({ amount: '', paid_date: today, mode: 'upi', reference_number: '', notes: '' });
  const [waForm, setWaForm] = useState({ message: '' });
  const [emailForm, setEmailForm] = useState({ message: '' });
  const [noteForm, setNoteForm] = useState({ note: '', next_call_date: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type });
      if (search) params.append('q', search);
      if (agingFilter !== 'all') params.append('aging', agingFilter);
      const r = await axios.get(`${API}/payment-followup/list?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [API, type, search, agingFilter]);

  useEffect(() => { load(); }, [load]);

  // ---- Mark Paid
  const openMarkPaid = (row) => {
    const balance = type === 'recurring' ? row.recurring_balance : row.non_recurring_balance;
    const full = type === 'recurring' ? row.recurring_amount : row.non_recurring_amount;
    const expected = balance && balance > 0 ? balance : full;
    setPayForm({
      amount: expected ? String(expected) : '',
      paid_date: today, mode: 'upi', reference_number: '', notes: '',
    });
    setMarkPaid({ open: true, row });
  };
  const submitMarkPaid = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) {
      toast.error('Amount must be > 0');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/payment-followup/record`, {
        customer_id: markPaid.row.customer_id,
        type,
        amount: parseFloat(payForm.amount),
        paid_date: payForm.paid_date,
        mode: payForm.mode,
        reference_number: payForm.reference_number || null,
        notes: payForm.notes || null,
      });
      toast.success('Payment recorded');
      setMarkPaid({ open: false, row: null });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- WhatsApp
  const openWa = (row) => { setWaForm({ message: '' }); setWaDialog({ open: true, row }); };
  const submitWa = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payment-followup/whatsapp`, {
        customer_id: waDialog.row.customer_id,
        type,
        custom_message: waForm.message || null,
      });
      toast.success('WhatsApp reminder sent');
      setWaDialog({ open: false, row: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Email
  const openEmail = (row) => { setEmailForm({ message: '' }); setEmailDialog({ open: true, row }); };
  const submitEmail = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payment-followup/email-reminder`, {
        customer_id: emailDialog.row.customer_id,
        type,
        custom_message: emailForm.message || null,
      });
      toast.success('Email reminder sent');
      setEmailDialog({ open: false, row: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Notes
  const openNote = (row) => { setNoteForm({ note: '', next_call_date: '' }); setNoteDialog({ open: true, row }); };
  const submitNote = async () => {
    if (!noteForm.note.trim()) { toast.error('Note is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/payment-followup/note`, {
        customer_id: noteDialog.row.customer_id,
        note: noteForm.note,
        next_call_date: noteForm.next_call_date || null,
      });
      toast.success('Note saved');
      setNoteDialog({ open: false, row: null });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- History
  const openHistory = async (row) => {
    try {
      const r = await axios.get(`${API}/payment-followup/history/${row.customer_id}`);
      setHistoryDialog({ open: true, row, records: r.data.records || [], notes: r.data.notes || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    }
  };

  // ---- Notify assignee
  const notifyAssignee = async (row) => {
    if (!row.payment_followup_assignee_email) {
      toast.error('No assignee set. Open Customer → Edit Payment Follow-up to assign.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/payment-followup/notify-assignee`, {
        customer_id: row.customer_id, type, channel: 'both',
      });
      const channels = (r.data?.channels || []).join(' + ') || 'none';
      toast.success(`Notified ${row.payment_followup_assignee_email} via ${channels}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to notify');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-followup-page">
      <Tabs value={type} onValueChange={(v) => { setType(v); setAgingFilter('all'); }}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="recurring" data-testid="followup-tab-recurring">
            <IndianRupee className="h-4 w-4 mr-1" /> Recurring Due
          </TabsTrigger>
          <TabsTrigger value="non_recurring" data-testid="followup-tab-non-recurring">
            <AlertTriangle className="h-4 w-4 mr-1" /> Non-Recurring Due
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <Card className={`cursor-pointer transition-shadow ${agingFilter === 'all' ? 'ring-2 ring-slate-700' : ''}`}
              onClick={() => setAgingFilter('all')}>
          <CardContent className="py-2 px-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total</p>
            <p className="text-lg font-bold text-slate-900" data-testid="followup-stat-total">{data.count}</p>
            <p className="text-[10px] text-slate-500">{RS(data.total_amount)}</p>
          </CardContent>
        </Card>
        {Object.entries(AGING_META)
          .sort((a, b) => a[1].rank - b[1].rank)
          .filter(([k]) => k !== 'no_due_date' || (data.aging_counts?.[k] || 0) > 0)
          .map(([key, meta]) => (
            <Card key={key}
                  className={`cursor-pointer transition-shadow ${agingFilter === key ? 'ring-2 ring-slate-700' : ''}`}
                  onClick={() => setAgingFilter(key)}>
              <CardContent className="py-2 px-3">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">{meta.label}</p>
                <p className="text-lg font-bold text-slate-900" data-testid={`followup-stat-${key}`}>
                  {data.aging_counts?.[key] || 0}
                </p>
              </CardContent>
            </Card>
          ))}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="py-3 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Search by name, code, contact, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="followup-search-input"
            />
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </CardContent>
      </Card>

      {/* List */}
      <div className="space-y-3" data-testid="followup-list">
        {data.rows.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-slate-500">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <p>No customers due in this bucket. Great job!</p>
            </CardContent>
          </Card>
        ) : data.rows.map(row => {
          const aging = AGING_META[row.aging] || AGING_META['upcoming'];
          const amount = type === 'recurring' ? row.recurring_amount : row.non_recurring_amount;
          const paid = type === 'recurring' ? row.recurring_paid_current_cycle : row.non_recurring_paid;
          const balance = type === 'recurring' ? row.recurring_balance : row.non_recurring_balance;
          const isPartial = paid > 0 && balance > 0;
          const due = type === 'recurring' ? row.recurring_next_due_date : row.non_recurring_due_date;
          return (
            <Card key={row.customer_id} data-testid={`followup-row-${row.customer_id}`}>
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Link to={`/customers/${row.customer_id}`}
                            className="font-semibold text-slate-900 hover:text-indigo-600 truncate">
                        {row.company_name}
                      </Link>
                      <Badge className={aging.color}>{aging.label}</Badge>
                      {row.customer_code && (
                        <span className="text-xs text-slate-500 font-mono">{row.customer_code}</span>
                      )}
                      {isPartial && (
                        <Badge className="bg-blue-100 text-blue-700">Partial Paid</Badge>
                      )}
                      {row.payment_followup_assignee_email && (
                        <Badge variant="outline" className="text-xs">
                          👤 {row.payment_followup_assignee_email.split('@')[0]}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Contact</p>
                        <p className="truncate">{row.contact_person || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Phone</p>
                        <p className="font-mono">{row.contact_phone || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Total</p>
                        <p className="font-semibold text-slate-800" data-testid={`row-total-${row.customer_id}`}>
                          {RS(amount)}
                        </p>
                        {type === 'recurring' && (
                          <p className="text-[10px] text-slate-400">every {row.recurring_cycle_days || 30}d</p>
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Paid</p>
                        <p className="font-semibold text-blue-700" data-testid={`row-paid-${row.customer_id}`}>
                          {RS(paid)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Balance · Due</p>
                        <p className="font-bold text-amber-700" data-testid={`row-balance-${row.customer_id}`}>
                          {RS(balance)}
                        </p>
                        <p className="text-[10px] text-slate-500">{due || '—'}</p>
                      </div>
                    </div>
                    {/* Progress bar when partial */}
                    {amount > 0 && (
                      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${paid >= amount ? 'bg-emerald-500' : isPartial ? 'bg-blue-500' : 'bg-slate-300'}`}
                          style={{ width: `${Math.min(100, (paid / amount) * 100).toFixed(0)}%` }}
                        />
                      </div>
                    )}
                    {row.payment_followup_notes && row.payment_followup_notes.length > 0 && (
                      <div className="mt-2 text-xs text-slate-500 bg-slate-50 border rounded p-2">
                        <span className="font-medium">Latest note:</span>{' '}
                        {row.payment_followup_notes[row.payment_followup_notes.length - 1].note}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    {row.contact_phone && (
                      <a href={`tel:${row.contact_phone}`}>
                        <Button size="sm" variant="outline" data-testid={`followup-call-${row.customer_id}`}>
                          <Phone className="h-4 w-4 mr-1" /> Call
                        </Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline"
                            onClick={() => openWa(row)}
                            disabled={!row.contact_phone}
                            data-testid={`followup-whatsapp-${row.customer_id}`}>
                      <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => openEmail(row)}
                            disabled={!row.contact_email}
                            data-testid={`followup-email-${row.customer_id}`}>
                      <Mail className="h-4 w-4 mr-1" /> Email
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => notifyAssignee(row)}
                            disabled={!row.payment_followup_assignee_email || submitting}
                            title={row.payment_followup_assignee_email ? `Ping ${row.payment_followup_assignee_email}` : 'No assignee set'}
                            data-testid={`followup-notify-${row.customer_id}`}>
                      <BellRing className="h-4 w-4 mr-1" /> Notify
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => openNote(row)}
                            data-testid={`followup-note-${row.customer_id}`}>
                      <NotebookPen className="h-4 w-4 mr-1" /> Note
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => openHistory(row)}
                            data-testid={`followup-history-${row.customer_id}`}>
                      <HistoryIcon className="h-4 w-4 mr-1" /> History
                    </Button>
                    <Button size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => openMarkPaid(row)}
                            data-testid={`followup-mark-paid-${row.customer_id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Paid
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Mark Paid Dialog */}
      <Dialog open={markPaid.open} onOpenChange={(o) => setMarkPaid({ open: o, row: markPaid.row })}>
        <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment — {markPaid.row?.company_name}</DialogTitle>
            <DialogDescription>
              Type: <span className="font-medium">{type === 'recurring' ? 'Recurring' : 'Non-Recurring'}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs.)</Label>
                <Input type="number" min="0" step="0.01"
                       value={payForm.amount}
                       onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                       data-testid="paid-amount-input" />
              </div>
              <div>
                <Label>Paid Date</Label>
                <Input type="date"
                       value={payForm.paid_date}
                       onChange={e => setPayForm({ ...payForm, paid_date: e.target.value })}
                       data-testid="paid-date-input" />
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={payForm.mode}
                      onValueChange={v => setPayForm({ ...payForm, mode: v })}>
                <SelectTrigger data-testid="paid-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number (optional)</Label>
              <Input value={payForm.reference_number}
                     onChange={e => setPayForm({ ...payForm, reference_number: e.target.value })}
                     placeholder="UPI / Cheque / Txn ID"
                     data-testid="paid-ref-input" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2}
                        value={payForm.notes}
                        onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                        data-testid="paid-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaid({ open: false, row: null })}>Cancel</Button>
            <Button onClick={submitMarkPaid} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="paid-submit-btn">
              {submitting ? 'Recording…' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={waDialog.open} onOpenChange={(o) => setWaDialog({ open: o, row: waDialog.row })}>
        <DialogContent className="sm:max-w-md" data-testid="wa-reminder-dialog">
          <DialogHeader>
            <DialogTitle>WhatsApp Reminder — {waDialog.row?.company_name}</DialogTitle>
            <DialogDescription>To: {waDialog.row?.contact_phone}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Custom Message (optional)</Label>
            <Textarea rows={5}
                      value={waForm.message}
                      onChange={e => setWaForm({ message: e.target.value })}
                      placeholder="Leave blank to send the default reminder template"
                      data-testid="wa-message-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={submitWa} disabled={submitting} className="bg-green-600 hover:bg-green-700"
                    data-testid="wa-send-btn">
              {submitting ? 'Sending…' : 'Send WhatsApp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => setEmailDialog({ open: o, row: emailDialog.row })}>
        <DialogContent className="sm:max-w-md" data-testid="email-reminder-dialog">
          <DialogHeader>
            <DialogTitle>Email Reminder — {emailDialog.row?.company_name}</DialogTitle>
            <DialogDescription>To: {emailDialog.row?.contact_email}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Custom Message (optional)</Label>
            <Textarea rows={6}
                      value={emailForm.message}
                      onChange={e => setEmailForm({ message: e.target.value })}
                      placeholder="Leave blank to send the default reminder template"
                      data-testid="email-message-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={submitEmail} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="email-send-btn">
              {submitting ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(o) => setNoteDialog({ open: o, row: noteDialog.row })}>
        <DialogContent className="sm:max-w-md" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>Follow-up Note — {noteDialog.row?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Note *</Label>
              <Textarea rows={3}
                        value={noteForm.note}
                        onChange={e => setNoteForm({ ...noteForm, note: e.target.value })}
                        placeholder="e.g. Spoke with finance, promised to pay by 25th"
                        data-testid="note-text-input" />
            </div>
            <div>
              <Label>Next Call Date (optional)</Label>
              <Input type="date"
                     value={noteForm.next_call_date}
                     onChange={e => setNoteForm({ ...noteForm, next_call_date: e.target.value })}
                     data-testid="note-next-call-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={submitNote} disabled={submitting} data-testid="note-save-btn">
              {submitting ? 'Saving…' : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(o) => setHistoryDialog({ ...historyDialog, open: o })}>
        <DialogContent className="sm:max-w-2xl" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HistoryIcon className="h-5 w-5" /> {historyDialog.row?.company_name}
              <Link to={`/customers/${historyDialog.row?.customer_id}`} className="text-indigo-600 ml-auto">
                <ExternalLink className="h-4 w-4" />
              </Link>
            </DialogTitle>
            <DialogDescription>Payment records & follow-up notes</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-2">Recent Payments</p>
              {historyDialog.records.length === 0 ? (
                <p className="text-sm text-slate-500">No payments recorded yet.</p>
              ) : historyDialog.records.map(r => (
                <div key={r.record_id} className="border rounded p-2 mb-2 text-sm bg-emerald-50/40">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-semibold text-emerald-700">{RS(r.amount)}</span>
                      <span className="text-xs text-slate-500 ml-2">{r.type}</span>
                    </div>
                    <span className="text-xs text-slate-500">{r.paid_date}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Mode: <span className="font-medium">{r.mode}</span>
                    {r.reference_number && <> · Ref: <span className="font-mono">{r.reference_number}</span></>}
                  </p>
                  {r.notes && <p className="text-xs text-slate-500 mt-1">{r.notes}</p>}
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-2">Follow-up Notes</p>
              {historyDialog.notes.length === 0 ? (
                <p className="text-sm text-slate-500">No notes yet.</p>
              ) : [...historyDialog.notes].reverse().map(n => (
                <div key={n.note_id} className="border rounded p-2 mb-2 text-sm bg-slate-50">
                  <div className="flex justify-between items-start">
                    <p className="flex-1">{n.note}</p>
                    {n.next_call_date && (
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-2">
                        <Clock className="h-3 w-3 mr-1" /> Next: {n.next_call_date}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">{n.by} · {n.at?.slice(0, 19).replace('T', ' ')}</p>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
